/**
 * AEOApprovalPanel Component
 *
 * Panel for managing AEO content approval workflow.
 * Shows status, feedback summary, and approval/rejection controls.
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Send,
  User,
  Calendar,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { authFetch } from '@/utils/auth-fetch';
import { cn } from '@/lib/utils';

type ContentStatus = 'draft' | 'pending_review' | 'approved' | 'rejected';

interface ContentMetadata {
  id: string;
  status: ContentStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdByName?: string;
  approvedByName?: string;
  approvedAt?: string;
  rejectionReason?: string;
}

interface FeedbackSummary {
  thumbsUp: number;
  thumbsDown: number;
  comments: number;
  unresolved: number;
}

interface AEOApprovalPanelProps {
  contentId: string;
  metadata: ContentMetadata;
  feedbackSummary?: FeedbackSummary;
  onStatusChange?: (newStatus: ContentStatus) => void;
  onRegenerate?: () => void;
  className?: string;
}

const statusConfig: Record<ContentStatus, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}> = {
  draft: {
    label: 'Draft',
    icon: <Clock className="h-4 w-4" />,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
  },
  pending_review: {
    label: 'Pending Review',
    icon: <Send className="h-4 w-4" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  approved: {
    label: 'Approved',
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  rejected: {
    label: 'Rejected',
    icon: <XCircle className="h-4 w-4" />,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
};

export function AEOApprovalPanel({
  contentId,
  metadata,
  feedbackSummary,
  onStatusChange,
  onRegenerate,
  className,
}: AEOApprovalPanelProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const status = statusConfig[metadata.status];

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const updateStatus = async (newStatus: ContentStatus, notes?: string) => {
    setIsLoading(true);
    try {
      const response = await authFetch(`/category/aeo-content/${contentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, notes }),
      });

      if (response.ok) {
        onStatusChange?.(newStatus);
        setShowApproveDialog(false);
        setShowRejectDialog(false);
        setRejectionReason('');
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasUnresolvedIssues = feedbackSummary && feedbackSummary.unresolved > 0;

  return (
    <>
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Content Review</span>
            <Badge
              variant="outline"
              className={cn(status.bgColor, status.color, 'flex items-center gap-1')}
            >
              {status.icon}
              {status.label}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Metadata Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Version</span>
              <p className="font-medium">{metadata.version}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Updated</span>
              <p className="font-medium">{formatDate(metadata.updatedAt)}</p>
            </div>
            {metadata.createdByName && (
              <div>
                <span className="text-muted-foreground">Created By</span>
                <p className="font-medium flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {metadata.createdByName}
                </p>
              </div>
            )}
            {metadata.approvedByName && metadata.approvedAt && (
              <div>
                <span className="text-muted-foreground">Approved By</span>
                <p className="font-medium flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {metadata.approvedByName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(metadata.approvedAt)}
                </p>
              </div>
            )}
          </div>

          {/* Feedback Summary */}
          {feedbackSummary && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">Feedback Summary</h4>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <ThumbsUp className="h-4 w-4 text-green-600" />
                  {feedbackSummary.thumbsUp} positive
                </span>
                <span className="flex items-center gap-1">
                  <ThumbsDown className="h-4 w-4 text-red-600" />
                  {feedbackSummary.thumbsDown} negative
                </span>
                <span className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                  {feedbackSummary.comments} comments
                </span>
              </div>
            </div>
          )}

          {/* Unresolved Issues Warning */}
          {hasUnresolvedIssues && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {feedbackSummary!.unresolved} unresolved issue(s) need attention before approval.
              </AlertDescription>
            </Alert>
          )}

          {/* Rejection Reason */}
          {metadata.status === 'rejected' && metadata.rejectionReason && (
            <Alert>
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Rejection reason:</strong> {metadata.rejectionReason}
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 pt-2">
            {metadata.status === 'draft' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateStatus('pending_review')}
                disabled={isLoading}
              >
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            )}

            {(metadata.status === 'draft' || metadata.status === 'pending_review') && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setShowApproveDialog(true)}
                  disabled={isLoading || hasUnresolvedIssues}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={isLoading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </>
            )}

            {metadata.status === 'rejected' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => updateStatus('draft')}
                disabled={isLoading}
              >
                <Clock className="h-4 w-4 mr-2" />
                Move to Draft
              </Button>
            )}

            {onRegenerate && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRegenerate}
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Content</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this AEO content?
              Once approved, it will be available for export and may be synced to other systems.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => updateStatus('approved')}
              disabled={isLoading}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Content</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection. This will help the content creator understand what needs to be improved.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => updateStatus('rejected', rejectionReason)}
              disabled={isLoading || !rejectionReason.trim()}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AEOApprovalPanel;
